import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function RichmondUponThamesCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Richmond Pothole Claims | 50% of U-Roads Need Resurfacing | Fixtyer"
        description="Richmond upon Thames earns a RED DfT condition scorecard despite GREEN spend. The council's own 2021/22 survey flagged 50% of unclassified roads for resurfacing. See what this means for your pothole claim under Section 58."
        canonical="/councils/richmond-upon-thames"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Richmond upon Thames', url: '/councils/richmond-upon-thames' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Richmond has a GREEN spend rating and is tripling capital investment — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Richmond projects £7.0m capital spend against a £664,000 DfT allocation in 2025/26, with preventative maintenance rising to 74.29%. But the Condition scorecard is RED — the DfT flags incomplete condition data — and the council\'s last published network survey dates from 2021/22. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council plans to spend next year.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 288.8km — 76% of Richmond\'s 380km carriageway network. The council\'s last Detailed Visual Inspection in 2021/22 found 50% of unclassified roads had defects requiring intervention such as resurfacing — roughly 144km of residential streets. Annex B data submitted to the DfT lists 9.1% of principal roads and 6.88% of non-principal classified roads in RED condition where maintenance should be considered, but the borough-wide U-road RAG breakdown is not published in the same format.',
          },
          {
            question: 'Why does the DfT rate Richmond\'s condition RED when the council says investment is rising?',
            answer: 'The DfT notes that Richmond\'s overall, condition and best-practice scorecards are "based on incomplete road condition data." The transparency report itself cites only a 2021/22 DVI survey for network condition percentages and admits the highway asset has been in "managed decline" for years. Capital spend is projected to jump from £2.7m to £7.0m in 2025/26 — but that is forward investment against a condition picture that is already years out of date.',
          },
          {
            question: 'Does Richmond sharing highway services with Wandsworth affect my claim?',
            answer: 'Richmond and Wandsworth operate a Shared Service Arrangement for all highway maintenance functions, with performance directly compared between boroughs. Liability for your claim still rests with Richmond upon Thames Council as the highway authority for roads in this borough. The shared-service model means Richmond may invoke Wandsworth-benchmarked processes under Section 58 — but the council\'s own June 2025 report also contains copy-pasted Wandsworth references, which is a transparency weakness worth noting if the council cites systematic maintenance records.',
          },
          {
            question: 'Richmond attends 100% of dangerous defects within 24 hours — does that block my claim?',
            answer: 'It helps the council only if they can prove your defect was classified as dangerous, notified through their Brightly Confirm system, and attended within 24 hours before your incident. The KPI RCS-HOS-002 showed 100% attendance in Q1 and Q2 of 2025/26 — but reactive maintenance still accounted for 66.67% of total maintenance spend as recently as 2024/25. Prior reports, photos showing defect age, and proof the pothole predated any inspection all matter when a council points to response-time KPIs.',
          },
          {
            question: 'TfL cut A-road funding — does that strengthen a claim on a main road?',
            answer: 'Potentially. Richmond states TfL funding for the Borough Principal Road Network "continues to fall short of what is needed, which has had a negative effect on the condition of the Borough\'s A roads." Only £200,000 of TfL BPRN funding was secured in 2023/24, 2024/25 and 2025/26. The council\'s 2021/22 DVI survey flagged 44% of A-roads for intervention, and Annex B reports 9.1% of principal roads in RED where maintenance should be considered — an admission of underfunded main-road maintenance the council documents itself.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Richmond upon Thames Council"
        councilSlug="richmond-upon-thames"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/richmond-upon-thames"
        headline="Richmond upon Thames Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Richmond upon Thames</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Richmond: Half of U-Roads Flagged for Resurfacing — DfT Condition RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Richmond upon Thames earns <strong className="text-emerald-400">GREEN</strong> for spend — projecting
            {' '}<strong className="text-emerald-400">£7.0m</strong> in capital works against a
            {' '}<strong className="text-emerald-400">£664,000</strong> DfT allocation. Yet the DfT awards
            {' '}<strong className="text-rose-400">RED</strong> for condition, flags incomplete survey data,
            and the council's own last network survey — from <strong className="text-rose-400">2021/22</strong> —
            found <strong className="text-rose-400">50% of unclassified roads</strong> had defects requiring
            intervention such as resurfacing, on a 380km network where 288.8km is residential.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">50%</div>
            <div className="text-white font-medium mt-2">U-roads needing intervention (2021/22 DVI)</div>
            <div className="text-brand-300 text-sm mt-1">
              Richmond's last published Detailed Visual Inspection — four years before this June 2025 report —
              flagged half of 288.8km of unclassified roads for resurfacing-level work. A-roads were 44% and
              B/C roads 43% on the same stale survey.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 380km Network — Three-Quarters Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Richmond upon Thames Council's own highway inventory from the June 2025 transparency report
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">380 km</div>
              <div className="text-sm text-surface-600 mt-1">Total carriageways</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">49.2 km</div>
              <div className="text-sm text-surface-600 mt-1">A-roads (12.9%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">42.0 km</div>
              <div className="text-sm text-surface-600 mt-1">B and C roads (11.1%)</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-rose-600">288.8 km</div>
              <div className="text-sm text-rose-800 mt-1">U-roads (76.0%)</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Section 41 — The Duty</h3>
              <p className="text-surface-600 text-sm">
                Richmond states it has "a statutory duty to maintain and manage its highway asset in a safe
                and reliable condition under Section 41 of the Highway Act 1980." The borough maintains
                approximately 380km of carriageways and 700km of footways, plus 85 highway bridges,
                17,828 gullies and 15,220 street lighting columns.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">DfT RED Condition Flag</h3>
              <p className="text-rose-800 text-sm">
                The DfT explicitly notes Richmond's overall, condition and best-practice scorecards are
                "based on incomplete road condition data, which has affected these ratings." That is the
                government's own caveat — not our characterisation — and it sits alongside a RED
                condition scorecard on the 2025/26 ratings table.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            The last Detailed Visual Inspection was in 2021/22 — supplemented by DfT single-data-list
            submissions in Annex B (October 2025)
          </p>

          {/* DVI intervention table */}
          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <div className="bg-surface-100 px-6 py-3 border-b border-surface-200">
              <h3 className="font-semibold text-brand-900">2021/22 DVI — roads requiring intervention (resurfacing-level defects)</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-50 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Network length</th>
                  <th className="px-6 py-3 text-center font-semibold text-rose-700">Requiring intervention</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Approx. km affected</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Principal (A) roads</td>
                  <td className="px-6 py-4 text-center">49.2 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">44%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~21.6 km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Non-principal (B and C) roads</td>
                  <td className="px-6 py-4 text-center">42.0 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">43%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~18.1 km</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-bold text-rose-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-bold">288.8 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">50%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-700">~144.4 km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Pavements / footways</td>
                  <td className="px-6 py-4 text-center">700 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">49%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~343 km</td>
                </tr>
              </tbody>
            </table>
            <div className="px-6 py-4 bg-amber-50 border-t border-amber-200">
              <p className="text-amber-800 text-sm">
                These figures measure defects requiring intervention such as resurfacing — not the same
                RED/Amber/Green categories used in some other councils' five-year tables. Richmond has not
                published an updated DVI since 2021/22 in this report. Approximate km affected derived from
                published percentages × network length.
              </p>
            </div>
          </div>

          {/* Annex B RAG data */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">Annex B — DfT single data list (where maintenance should be considered)</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-surface-50 rounded-xl p-5 border border-surface-200">
                <div className="text-sm text-surface-500 mb-2">130-01 Principal roads</div>
                <div className="flex gap-6">
                  <div>
                    <div className="text-2xl font-bold text-rose-600">9.1%</div>
                    <div className="text-xs text-rose-800">Red</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-600">32.2%</div>
                    <div className="text-xs text-amber-800">Amber</div>
                  </div>
                </div>
              </div>
              <div className="bg-surface-50 rounded-xl p-5 border border-surface-200">
                <div className="text-sm text-surface-500 mb-2">130-02 Non-principal classified roads</div>
                <div className="flex gap-6">
                  <div>
                    <div className="text-2xl font-bold text-rose-600">6.88%</div>
                    <div className="text-xs text-rose-800">Red</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-600">25.23%</div>
                    <div className="text-xs text-amber-800">Amber</div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-surface-600 text-sm mt-4">
              Skidding resistance data (130-03) shows 33.4% Red and 66.6% Green. No equivalent 130-series
              RAG breakdown for unclassified roads appears in Annex B — despite U-roads being 76% of the
              carriageway network.
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Based on the last Detailed Visual Inspection (DVI) conditions survey undertaken in 2021/22, the percentage of roads and pavement with defects that require intervention such as resurfacing, were as follows:"
              source="Richmond upon Thames Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Richmond must show it had a reasonable system for knowing
              the condition of its roads. Ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it before or after 2021/22?</li>
              <li>• If half of U-roads already needed intervention at the last survey, what was done about yours?</li>
              <li>• Why has no updated DVI been published in the June 2025 report if AI surveys are still being trialled?</li>
              <li>• Does the council's dangerous-defect KPI cover your pothole, or only defects reported through their system?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge from a four-year-old survey while the DfT
              rates its condition data incomplete.
            </p>
          </div>
        </div>
      </section>

      {/* Managed decline */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Managed Decline Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Richmond's own explanation for why condition lags spend — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The Council highway asset, like many other Highway Authorities' highway asset, has been in a managed decline position for a number of years."
              source="Richmond upon Thames Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="There has been a sustained challenges in highway maintenance investment going back many years with an ever-increasing gap between asset needs and available budgets. This is a national challenge which has resulted a deterioration of the condition of footways and carriageways."
              source="Richmond upon Thames Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Highways that are old and beyond their design life are increasingly fragile and less resilient to damage from wear and tear from heavier cars, buses and HGVs traffic and also more prevalent adverse weather conditions."
              source="Richmond upon Thames Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Admission Means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Richmond formally acknowledges its highway asset has been in managed decline — accepting
                deterioration rather than keeping pace with need. The council also states that once a road
                reaches a bad enough condition, "more expensive structural repairs, complete replacement or
                even closure are likely to follow."
              </p>
              <p className="text-surface-600 text-sm">
                When a council documents managed decline and an investment gap, it raises questions about
                whether reactive pothole patching satisfies the Section 41 duty of reasonable maintenance.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Investment Response</h3>
              <p className="text-amber-800 text-sm mb-3">
                Metis Consultants modelling showed "substantive additional investment would not only stop
                the present managed levels of decline but lead to better of the current network." The
                council's capital spend is projected to rise from £2.7m to £7.0m in 2025/26, with
                preventative maintenance jumping from 33.33% to 74.29%.
              </p>
              <p className="text-amber-800 text-sm">
                That is a plan — not proof your road was maintained before your incident. Forward spend
                does not retroactively fix a four-year-old condition survey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend, RED Condition
          </h2>
          <p className="text-surface-600 mb-8">
            Four years of Richmond's own spending data — and the sharp 2025/26 uplift
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital (£000s)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend (£m)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend (£m)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Preventative</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£2.70</td>
                  <td className="px-6 py-4 text-center">£1.7</td>
                  <td className="px-6 py-4 text-center">37.03%</td>
                  <td className="px-6 py-4 text-center text-rose-600">62.96%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£204</td>
                  <td className="px-6 py-4 text-center">£2.70</td>
                  <td className="px-6 py-4 text-center">£1.8</td>
                  <td className="px-6 py-4 text-center">33.33%</td>
                  <td className="px-6 py-4 text-center text-rose-600">66.67%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£204</td>
                  <td className="px-6 py-4 text-center">£2.70</td>
                  <td className="px-6 py-4 text-center">£1.8</td>
                  <td className="px-6 py-4 text-center">33.33%</td>
                  <td className="px-6 py-4 text-center text-rose-600">66.67%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26</td>
                  <td className="px-6 py-4 text-center font-bold">£664</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£7.00</td>
                  <td className="px-6 py-4 text-center font-bold">£1.8</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">74.29%</td>
                  <td className="px-6 py-4 text-center font-bold">25.71%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-2xl font-bold mb-4">More Than Ten Times The DfT Allocation</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£664k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£7.0m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">74.29%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Richmond plans to spend more than <span className="text-emerald-400">ten times its DfT allocation</span> —
              yet the DfT still rates condition <span className="text-rose-400">RED</span>. Revenue funding
              is "spent on reactive maintenance such as potholes and defective paving repairs" while capital
              funds resurfacing — and reactive work still dominated at 66.67% as recently as 2024/25.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Gross Replacement Cost Context</h3>
              <p className="text-surface-600 text-sm mb-3">
                Annex B estimates the gross replacement cost of A, B and U roads at £685,835,000 — with
                footway categories 1–3 at £158,504,000. Total maintenance spend in 2024/25 represented
                just 0.06% of current carriageway and footway asset value.
              </p>
              <p className="text-surface-600 text-sm">
                Richmond's own modelling showed it would take "vast investment" to reach 0% defects — the
                council is investing more, but from a low base relative to asset value.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">2025/26 DfT Programme Streets</h3>
              <p className="text-amber-800 text-sm mb-3">
                Additional DfT funding in 2025/26 is allocated to named works including Alexandra Road
                (East Sheen), The Byeway (North Richmond), Hanworth Road footways, Victoria Road footways,
                St Margaret's Road footway extension, and "Bus Stops and Potholes Repairs Areas throughout
                the borough (Carriageway Repair)."
              </p>
              <p className="text-amber-800 text-sm">
                If your pothole was not on a programmed street, the council may argue it was outside the
                current investment pipeline — which makes prior reports and photos of your specific defect
                more important.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TfL and Wandsworth */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            TfL Funding Gap and the Wandsworth Shared Service
          </h2>
          <p className="text-surface-600 mb-8">
            Underfunded A-roads and a cross-borough maintenance arrangement — with copy-paste errors in
            Richmond's own report
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="TfL funding for the BPRN continues to fall short of what is needed, which has had a negative effect on the condition of the Borough's A roads."
              source="Richmond upon Thames Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Richmond and Wandsworth Borough Council operate a Shared Service Arrangement for all Highway Maintenance functions, so the performance of each borough is directly compared."
              source="Richmond upon Thames Council Local Highways Maintenance Transparency Report — Annex B (October 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">TfL BPRN: £200k a Year</h3>
              <p className="text-surface-600 text-sm mb-3">
                Richmond previously received an annual TfL grant for the Borough Principal Road Network
                until 2018/19. TfL funding has been "considerably reduced" and is now limited to a central
                amount boroughs must bid for competitively. Richmond secured £200,000 in 2023/24, 2024/25
                and 2025/26.
              </p>
              <p className="text-surface-600 text-sm">
                With 44% of A-roads flagged for intervention at the last DVI and 9.1% in RED on the DfT data
                list, the council documents underfunding of its most trafficked routes.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Wandsworth Copy-Paste Problem</h3>
              <p className="text-amber-800 text-sm mb-3">
                Richmond's June 2025 report states "Wandsworth Borough Council will ensure that any
                contractor appointed for future condition surveys holds this accreditation" when describing
                PAS 2161 transition — and references "a more sustainable Wandsworth" in its FM Conway
                contract section.
              </p>
              <p className="text-amber-800 text-sm">
                These are Richmond's published words, not ours. They suggest template text shared with
                Wandsworth and weaken confidence in borough-specific condition reporting — relevant if the
                council cites systematic asset management under Section 58.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Inspection and Response KPIs</h3>
            <p className="text-brand-200 mb-4">
              Richmond carries out UKPMS-conforming DVI surveys, validated by engineer inspections, plus
              safety inspections on principal and non-principal roads under the 2016 Code of Practice.
              Hospitals, schools and busy routes are inspected more frequently.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 mb-4">
              <div className="bg-brand-900/50 rounded-xl p-5">
                <div className="text-3xl font-bold text-emerald-400">100%</div>
                <div className="text-brand-300 text-sm mt-1">RCS-HOS-002: dangerous defect attendance within 24hrs (Q1–Q2 2025/26)</div>
              </div>
              <div className="bg-brand-900/50 rounded-xl p-5">
                <div className="text-3xl font-bold text-amber-400">2026/27</div>
                <div className="text-brand-300 text-sm mt-1">PAS 2161 condition methodology due nationally — AI surveys still being trialled</div>
              </div>
            </div>
            <p className="text-white font-medium">
              Public complaints and defect reports are logged on the Brightly Confirm system — "the numbers
              of Carriageway or Footway Defects is one of Factors that are included" in maintenance
              programme selection. Prior reports through that system or FixMyStreet are direct evidence of notice.
            </p>
          </div>
        </div>
      </section>

      {/* Claims balance */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Condition, GREEN-Spend Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Richmond is investing heavily — but condition data tells a different story
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital projected at £7.0m against £664k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative share rising to 74.29% in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% dangerous-defect attendance KPI in Q1–Q2 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> FM Conway contract, UKPMS DVI surveys and published annual maintenance programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> LoTAG/LoHEG benchmarking and shared-service performance comparison with Wandsworth
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence citing inspection regimes and response KPIs. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What Works In Yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED condition scorecard — DfT flags incomplete road condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Last network DVI from 2021/22 — 50% of U-roads flagged for intervention
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "managed decline" and an ever-increasing investment gap
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 66.67% reactive maintenance spend as recently as 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> TfL A-road funding "falls short" — 44% of A-roads needed intervention at last survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Wandsworth copy-paste errors in Richmond's published transparency report
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend and a 100% dangerous-defect KPI, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (Brightly Confirm, FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 2021/22 survey gap and 50% intervention rate are structural arguments</li>
              <li>• Whether your street was on the 2025/26 DfT-funded programme list or outside the pipeline</li>
              <li>• Proximity to recent utility streetworks — Richmond operates a permit scheme with inspection and enforcement</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Richmond's own transparency data — including managed decline and stale survey admissions — where they help you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Richmond upon Thames?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded borough with RED condition data demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 2021/22 survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No managed-decline citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 50% U-road intervention rate documented</li>
                <li>✅ RED condition / incomplete data argued</li>
                <li>✅ Managed decline admission cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Richmond</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=richmond-upon-thames"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Richmond Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Richmond has a GREEN spend rating and is tripling capital investment — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Richmond projects £7.0m capital spend against a
                £664,000 DfT allocation in 2025/26, with preventative maintenance rising to 74.29%. But the
                Condition scorecard is RED — the DfT flags incomplete condition data — and the council's last
                published network survey dates from 2021/22. Section 58 turns on whether the specific defect
                that damaged your vehicle was reasonably inspected and repaired, not on how much the council
                plans to spend next year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 288.8km — 76% of Richmond's 380km carriageway network. The council's last
                Detailed Visual Inspection in 2021/22 found 50% of unclassified roads had defects requiring
                intervention such as resurfacing — roughly 144km of residential streets. Annex B data submitted
                to the DfT lists 9.1% of principal roads and 6.88% of non-principal classified roads in RED
                condition where maintenance should be considered, but the borough-wide U-road RAG breakdown is
                not published in the same format.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does the DfT rate Richmond's condition RED when the council says investment is rising?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT notes that Richmond's overall, condition and best-practice scorecards are "based on
                incomplete road condition data." The transparency report itself cites only a 2021/22 DVI survey
                for network condition percentages and admits the highway asset has been in "managed decline" for
                years. Capital spend is projected to jump from £2.7m to £7.0m in 2025/26 — but that is forward
                investment against a condition picture that is already years out of date.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Richmond sharing highway services with Wandsworth affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Richmond and Wandsworth operate a Shared Service Arrangement for all highway maintenance
                functions, with performance directly compared between boroughs. Liability for your claim still
                rests with Richmond upon Thames Council as the highway authority for roads in this borough. The
                shared-service model means Richmond may invoke Wandsworth-benchmarked processes under Section 58
                — but the council's own June 2025 report also contains copy-pasted Wandsworth references, which is
                a transparency weakness worth noting if the council cites systematic maintenance records.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Richmond attends 100% of dangerous defects within 24 hours — does that block my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It helps the council only if they can prove your defect was classified as dangerous, notified
                through their Brightly Confirm system, and attended within 24 hours before your incident. The KPI
                RCS-HOS-002 showed 100% attendance in Q1 and Q2 of 2025/26 — but reactive maintenance still
                accounted for 66.67% of total maintenance spend as recently as 2024/25. Prior reports, photos
                showing defect age, and proof the pothole predated any inspection all matter when a council points
                to response-time KPIs.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                TfL cut A-road funding — does that strengthen a claim on a main road?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially. Richmond states TfL funding for the Borough Principal Road Network "continues to fall
                short of what is needed, which has had a negative effect on the condition of the Borough's A
                roads." Only £200,000 of TfL BPRN funding was secured in 2023/24, 2024/25 and 2025/26. The
                council's 2021/22 DVI survey flagged 44% of A-roads for intervention, and Annex B reports 9.1% of
                principal roads in RED where maintenance should be considered — an admission of underfunded
                main-road maintenance the council documents itself.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://richmond.gov.uk/media/jzbd0uby/richmond_local_highway_maintenance_transparency_report_june_2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Richmond upon Thames Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
